import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Button,
  Checkbox,
  DialogOverlay,
  DialogOverlayActions,
  DialogOverlayFooter,
  IconButton,
  Paragraph,
  Separator,
} from '@fs/zion-ui'
import { Arrow } from '@fs/zion-icon'

/**
 * Asks who should be primary in a newly created group.
 *
 * Skipped when only one person is moving, since that person is auto-set primary. More than one
 * primary is legal, and confirming creates one shared group rather than one each.
 *
 * @param {Object} params
 * @param {Object} params.overlay - Controlled overlay from the flow; its `close` reports a dismissal
 *   rather than performing one, so X, Escape and click-away all arrive as `onCancel`
 * @param {Array<{id: string, name: string}>} params.movingPersons - Everyone moving into the new group
 * @param {boolean} [params.showBack=false] - Show Back; only true when the Previous Relationships step applied
 * @param {() => void} [params.onBack] - Called when the patron goes back a step
 * @param {() => void} [params.onCancel] - Called when the patron backs out, returning them to the
 *   selection sheet with their picks intact rather than abandoning the flow
 * @param {(primaryPersonIds: Array<string>) => void} [params.onConfirm] - Called with the chosen primary person ids
 * @returns {React.ReactElement}
 */
const ChoosePrimaryPersonDialog = ({
  overlay,
  movingPersons = [],
  showBack = false,
  onBack = () => {},
  onCancel = () => {},
  onConfirm = () => {},
}) => {
  const [t] = useTranslation()
  const [primaryPersonIds, setPrimaryPersonIds] = useState([])

  const togglePrimaryPerson = (personId) => {
    setPrimaryPersonIds((current) =>
      current.includes(personId) ? current.filter((id) => id !== personId) : [...current, personId]
    )
  }

  const handleConfirm = () => onConfirm(primaryPersonIds)

  return (
    <DialogOverlay
      {...overlay}
      title={t('add-existing-person.primary-person.title', 'Choose a primary person')}
      data-testid="choose-primary-person-dialog"
      size="sm"
    >
      {/* DialogOverlay has no header slot for a back control, so it leads the content instead.
          Only rendered when the Previous Relationships step actually applied. */}
      {showBack && (
        <>
          <IconButton
            Icon={Arrow}
            iconDirection="backward"
            size="xs"
            color="gray"
            onClick={onBack}
            aria-label={t('add-existing-person.primary-person.back.label', 'Back to Previous Relationships')}
            data-testid="choose-primary-person-back-button"
          />
          <Separator size="xxs" />
        </>
      )}
      <Paragraph size="sm">
        {t(
          'add-existing-person.primary-person.body',
          'Select who should be the primary person for this new group. You can select more than one.'
        )}
      </Paragraph>
      <Separator />
      {movingPersons.map((person) => (
        <Checkbox
          key={person.id}
          label={person.name}
          checked={primaryPersonIds.includes(person.id)}
          onChange={() => togglePrimaryPerson(person.id)}
          data-testid={`primary-person-checkbox-${person.id}`}
        />
      ))}
      <Separator />
      <DialogOverlayFooter>
        <DialogOverlayActions>
          <Button
            color="gray"
            onClick={onCancel}
            linkName="Choose Primary Person Cancel"
            data-testid="choose-primary-person-cancel-button"
          >
            {t('add-existing-person.primary-person.cancel.button', 'Cancel')}
          </Button>
          <Button
            emphasis="high"
            disabled={primaryPersonIds.length === 0}
            onClick={handleConfirm}
            linkName="Choose Primary Person Create Group"
            data-testid="choose-primary-person-create-button"
          >
            {t('add-existing-person.primary-person.create.button', 'Create Group')}
          </Button>
        </DialogOverlayActions>
      </DialogOverlayFooter>
    </DialogOverlay>
  )
}

export default ChoosePrimaryPersonDialog
